'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useCreateFile } from '@/hooks'
import { FileForm } from '@/components/partials/files'
import { type FileFormRef } from '@/components/partials/files/form'
import { CreateFileParams } from '@/types/file'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const fileFormRef = useRef<FileFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { mutate: createFile, isPending: isLoading, uploadProgress } = useCreateFile()

  const handleSubmit = (data: CreateFileParams) => {
    createFile(data, {
      onSuccess: async newFile => {
        setIsRedirecting(true)
        queryClient.setQueryData(['file', newFile.id], newFile)
        await queryClient.invalidateQueries({ queryKey: ['files'] })
        toast.success(t('created_successfully'))
        router.push(`/files/${newFile.id}`)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (fileFormRef.current) {
      fileFormRef.current.handleValidationErrors(error)
    }

    if (error.response?.status !== 422) {
      toast.error(t('error_creating'))
    }
  }

  const handleCancel = () => {
    router.push('/files')
  }

  return (
    <FileForm
      ref={fileFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
      uploadProgress={isLoading ? uploadProgress : undefined}
      optimizeImageSelection={true}
    />
  )
}

export default Create
